import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Feature, GeoJsonProperties, Polygon } from 'geojson';
import type mapboxgl from 'mapbox-gl';
import type { LngLatBoundsLike, LngLatLike, PaddingOptions } from 'mapbox-gl';
import { UserPreferencesBaseMap } from '../utils/user.enum';
import type MapLayer from './map-layer-manager/map-layer.enum';
import MapStyle from './map-style';

export const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
export const DEFAULT_MAP_CENTER: [number, number] = [-8.1, 39];
export const DEFAULT_MAP_ZOOM = 2;

export type MapExtent = {
  center: [number, number];
  zoom: number;
};

type MapStyleObject = {
  style: any;
  options: { diff?: boolean };
};

type FitBounds = {
  bounds: LngLatBoundsLike | null;
  options?: mapboxgl.MapboxOptions['fitBoundsOptions'];
};

export type LayerProperty = {
  layerId: MapLayer | null;
  name: string;
  value: any;
} | null;

interface InitialState {
  mapExtent: MapExtent;
  mapZoom: number;
  bathymetryOpacity: number;
  shippingTrafficV2Opacity: number;
  mapInitialised: boolean;
  style: string;
  drawType: string | null;
  mapStyle: MapStyleObject;
  paintProperty: LayerProperty;
  layoutProperty: { layerId: MapLayer; name: string; value: any }[];
  flyTo: {};
  padding: PaddingOptions;
  fitBounds: FitBounds;
  centre: LngLatLike | null;
  mapCircleCreatorActive: boolean;
  mapCircleFeature: Feature<Polygon, GeoJsonProperties> | null;
  colourList: number;
  previousColourIds: Record<string, number>;
  osmChecked: boolean;
  bathymetryChecked: boolean;
  shippingTrafficV2Checked: boolean;
  tempBaseMapSelected: UserPreferencesBaseMap;
}

export const INITIAL_MAP_STATE: InitialState = {
  mapExtent: {
    center: DEFAULT_MAP_CENTER,
    zoom: DEFAULT_MAP_ZOOM,
  },
  mapZoom: 2,
  bathymetryOpacity: 100,
  shippingTrafficV2Opacity: 100,
  mapInitialised: false,
  style: MapStyle.DEFAULT,
  drawType: null,
  mapStyle: {
    style: null,
    options: { diff: true },
  },
  paintProperty: {
    layerId: null,
    name: '',
    value: 0,
  },
  layoutProperty: [],
  flyTo: {},
  padding: { top: 0, left: 0, right: 0, bottom: 0 },
  fitBounds: {
    bounds: null,
  },
  centre: null,
  mapCircleCreatorActive: false,
  mapCircleFeature: null,
  colourList: 0,
  previousColourIds: {},
  osmChecked: false,
  bathymetryChecked: false,
  shippingTrafficV2Checked: false,
  tempBaseMapSelected: UserPreferencesBaseMap.DEFAULT,
};

const MapSlice = createSlice({
  name: 'map',
  initialState: INITIAL_MAP_STATE,
  reducers: {
    setStyle: (state, action) => {
      state.style = action.payload;
    },
    setMapInitialised: (state, action) => {
      state.mapInitialised = action.payload;
    },
    setMapExtent: (state, action) => {
      state.mapExtent = action.payload;
    },
    setDrawType: (state, action) => {
      state.drawType = action.payload;
    },
    setMapZoom: (state, action) => {
      state.mapZoom = action.payload;
    },
    setBathymetryOpacity: (state, action) => {
      state.bathymetryOpacity = action.payload;
    },
    setShippingTrafficV2Opacity: (state, action) => {
      state.shippingTrafficV2Opacity = action.payload;
    },
    setMapStyle: (
      state,
      action: PayloadAction<{ style: any | null; options?: { diff?: boolean } }>
    ) => {
      state.mapStyle = {
        style: action.payload.style,
        options: action.payload.options || { diff: true },
      };
    },
    setPaintProperty: (state, action) => {
      state.paintProperty = action.payload;
    },
    setFlyTo: (state, action) => {
      state.flyTo = action.payload;
    },
    setFitBounds: (state, action) => {
      state.fitBounds = action.payload;
    },
    setCentre: (state, action) => {
      state.centre = action.payload;
    },
    setMapCircleCreatorActive: (state, action) => {
      state.mapCircleCreatorActive = action.payload;
    },
    setMapCircleFeature: (state, action) => {
      state.mapCircleFeature = action.payload;
    },
    setColourListCount: (state, action) => {
      state.colourList = action.payload;
    },
    updatePreviousColourId: (state, action) => {
      state.previousColourIds = {
        ...state.previousColourIds,
        ...action.payload,
      };
    },
    setOsmChecked: (state, action) => {
      state.osmChecked = action.payload;
    },
    setBathymetryChecked: (state, action) => {
      state.bathymetryChecked = action.payload;
    },
    setTempBaseMapSelected: (state, action) => {
      state.tempBaseMapSelected = action.payload;
    },
    setShippingTrafficV2Checked: (state, action) => {
      state.shippingTrafficV2Checked = action.payload;
    },
  },
});

export const {
  setStyle,
  setMapZoom,
  setBathymetryOpacity,
  setShippingTrafficV2Opacity,
  setMapInitialised,
  setMapExtent,
  setDrawType,
  setMapStyle,
  setPaintProperty,
  setFlyTo,
  setFitBounds,
  setCentre,
  setMapCircleCreatorActive,
  setMapCircleFeature,
  setColourListCount,
  updatePreviousColourId,
  setOsmChecked,
  setBathymetryChecked,
  setTempBaseMapSelected,
  setShippingTrafficV2Checked,
} = MapSlice.actions;

export default MapSlice.reducer;
